import { useMemo, useState } from "react";
import type { RoleDto } from "@codestack/shared";
import { createRoleSchema } from "@codestack/shared";
import { Badge, Button, Input, Modal, Textarea } from "@codestack/ui";
import { apiRequest, ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorState, InlineAlert, LoadingState } from "../components/States";
import { titleCase } from "../lib/format";

type CreateForm = {
  name: string;
  slug: string;
  description: string;
  permissions: string[];
};

const emptyForm: CreateForm = {
  name: "",
  slug: "",
  description: "",
  permissions: [],
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function RolesPage() {
  const { can } = useAuth();
  const roles = useApi<RoleDto[]>("/admin/roles");
  const permissions = useApi<string[]>("/admin/roles/permissions");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [editing, setEditing] = useState<RoleDto | null>(null);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const canManage = can("role:manage");
  const allPermissions = permissions.data ?? [];

  const grouped = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const key of allPermissions) {
      const group = key.split(":")[0] ?? "other";
      const list = map.get(group) ?? [];
      list.push(key);
      map.set(group, list);
    }
    return Array.from(map.entries());
  }, [allPermissions]);

  const openCreate = () => {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError(null);
    setCreateOpen(true);
  };

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const parsed = createRoleSchema.safeParse({
      name: form.name.trim(),
      slug: form.slug.trim() || slugify(form.name),
      description: form.description.trim() || undefined,
      permissions: form.permissions,
    });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest("/admin/roles", { method: "POST", body: parsed.data });
      setCreateOpen(false);
      roles.reload();
    } catch (cause) {
      setFormError(
        cause instanceof ApiError ? cause.message : "The role could not be created.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (role: RoleDto) => {
    setEditing(role);
    setEditPermissions(role.permissions);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await apiRequest(`/admin/roles/${editing.id}`, {
        method: "PATCH",
        body: { permissions: editPermissions },
      });
      setEditing(null);
      roles.reload();
    } catch (cause) {
      setEditError(
        cause instanceof ApiError ? cause.message : "The role could not be updated.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const toggle = (
    list: string[],
    setList: (value: string[]) => void,
    permission: string,
  ) => {
    setList(
      list.includes(permission)
        ? list.filter((value) => value !== permission)
        : [...list, permission],
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">Roles & permissions</h1>
          <p className="text-sm text-ink-400">
            Permissions are enforced server-side on every request.
          </p>
        </div>
        {canManage ? <Button onClick={openCreate}>New role</Button> : null}
      </div>

      {roles.error ? <ErrorState message={roles.error} onRetry={roles.reload} /> : null}
      {roles.loading && !roles.data ? <LoadingState label="Loading roles…" /> : null}

      {roles.data && roles.data.length === 0 ? (
        <EmptyState title="No roles defined" description="Create a role to group permissions." />
      ) : null}

      {roles.data && roles.data.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {roles.data.map((role) => (
            <div key={role.id} className="admin-card flex flex-col p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-sm font-semibold text-white">{role.name}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-ink-500">{role.slug}</p>
                </div>
                {role.isSystem ? <Badge tone="info">System</Badge> : <Badge>Custom</Badge>}
              </div>

              <p className="mt-2 min-h-[2rem] text-[11px] leading-relaxed text-ink-500">
                {role.description ?? "No description."}
              </p>

              <div className="mt-3 flex flex-wrap gap-1">
                {role.permissions.slice(0, 4).map((permission) => (
                  <span
                    key={permission}
                    className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-[10px] text-ink-400"
                  >
                    {permission}
                  </span>
                ))}
                {role.permissions.length > 4 ? (
                  <span className="rounded bg-white/[0.05] px-1.5 py-0.5 font-mono text-[10px] text-ink-500">
                    +{role.permissions.length - 4}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-3">
                <span className="text-[11px] text-ink-500">
                  {role.userCount} user{role.userCount === 1 ? "" : "s"}
                </span>
                {canManage && !role.isSystem ? (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(role)}>
                    Edit
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create role"
        description="Group permissions into a reusable role."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" form="create-role-form" disabled={submitting}>
              {submitting ? "Creating…" : "Create role"}
            </Button>
          </>
        }
      >
        <form id="create-role-form" onSubmit={submitCreate} className="space-y-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              value={form.name}
              error={fieldErrors.name}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  name: event.target.value,
                  slug:
                    current.slug === "" || current.slug === slugify(current.name)
                      ? slugify(event.target.value)
                      : current.slug,
                }))
              }
              required
            />
            <Input
              label="Slug"
              value={form.slug}
              error={fieldErrors.slug}
              onChange={(event) => setForm({ ...form, slug: slugify(event.target.value) })}
              required
            />
          </div>

          <Textarea
            label="Description"
            rows={2}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />

          <div>
            <p className="mb-2 text-sm font-medium text-ink-800">Permissions</p>
            <div className="space-y-3">
              {grouped.map(([group, keys]) => (
                <div key={group}>
                  <p className="admin-label mb-1.5">{titleCase(group)}</p>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {keys.map((permission) => (
                      <label
                        key={permission}
                        className="flex cursor-pointer items-center gap-2 rounded-md border border-white/[0.07] px-2.5 py-1.5 hover:bg-white/[0.03]"
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(permission)}
                          onChange={() =>
                            toggle(form.permissions, (value) =>
                              setForm((current) => ({ ...current, permissions: value })),
                              permission,
                            )
                          }
                          className="h-3.5 w-3.5 rounded border-white/20 bg-transparent"
                        />
                        <span className="font-mono text-[11px] text-ink-300">{permission}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {fieldErrors.permissions ? (
              <p className="mt-1.5 text-xs text-red-400">{fieldErrors.permissions}</p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Edit ${editing.name}` : "Edit role"}
        description="Changes apply to every user holding this role."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={editSaving}>
              {editSaving ? "Saving…" : "Save permissions"}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            {editError ? <InlineAlert tone="danger">{editError}</InlineAlert> : null}
            {grouped.map(([group, keys]) => (
              <div key={group}>
                <p className="admin-label mb-1.5">{titleCase(group)}</p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {keys.map((permission) => (
                    <label
                      key={permission}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-white/[0.07] px-2.5 py-1.5 hover:bg-white/[0.03]"
                    >
                      <input
                        type="checkbox"
                        checked={editPermissions.includes(permission)}
                        onChange={() => toggle(editPermissions, setEditPermissions, permission)}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-transparent"
                      />
                      <span className="font-mono text-[11px] text-ink-300">{permission}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
